import React, { useState, useEffect } from 'react';
import {
  createHospital, getHospitals, updateHospital, deleteHospital, HospitalDeleteSummary,
  getOperatingRooms, createOperatingRoom, updateOperatingRoom, deleteOperatingRoom,
} from '../services/api.ts';
import AdminOnly from './AdminOnly.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { colorForHospital } from './hospitalColors.ts';

// Room-type presets for the "add rooms" control. The label used in room names is cleaned up
// (Generic OR → "OR", C/S (C-Section) → "C/S"); "Custom…" uses the free-text box.
const ROOM_PRESETS = ['Generic OR', 'Endo', 'C/S (C-Section)', 'MRI', 'CT', 'Specials', 'EP', 'Cath Lab', 'PACU', 'Custom…'];
const presetLabel = (preset: string, custom: string): string => {
  switch (preset) {
    case 'Generic OR': return 'OR';
    case 'C/S (C-Section)': return 'C/S';
    case 'Custom…': return (custom || '').trim();
    default: return preset;
  }
};
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HospitalForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    address: { street: '', city: '', state: '', zipCode: '', country: 'USA' },
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
  });
  const [createNumORs, setCreateNumORs] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [hospitals, setHospitals] = useState<any[]>([]);
  const [loadingHospitals, setLoadingHospitals] = useState(false);
  const [editingHospitalId, setEditingHospitalId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingHospitalId, setDeletingHospitalId] = useState<string | null>(null);
  const [savingColorId, setSavingColorId] = useState<string | null>(null);
  const [savingSiteTypeId, setSavingSiteTypeId] = useState<string | null>(null);
  const [createUseColor, setCreateUseColor] = useState(false);
  const [createColor, setCreateColor] = useState('#3b82f6');
  // Site type for the create form — see saveSiteType / backend utils/site-classification.ts.
  const [createIsSurgeryCenter, setCreateIsSurgeryCenter] = useState(false);
  const [createIsChildrensHospital, setCreateIsChildrensHospital] = useState(false);

  // Operating-room management
  const [operatingRooms, setOperatingRooms] = useState<any[]>([]);
  const [expandedHospitalId, setExpandedHospitalId] = useState<string | null>(null);
  const [roomPreset, setRoomPreset] = useState('Generic OR');
  const [roomCustom, setRoomCustom] = useState('');
  const [roomCount, setRoomCount] = useState('1');
  const [addingRooms, setAddingRooms] = useState(false);
  const [roomNameEdits, setRoomNameEdits] = useState<Record<string, string>>({});
  const [busyRoomId, setBusyRoomId] = useState<string | null>(null);

  useEffect(() => { loadHospitals(); loadOperatingRooms(); }, []);

  const loadHospitals = async () => {
    setLoadingHospitals(true);
    try {
      const response = await getHospitals();
      setHospitals(response.hospitals || []);
    } catch (error) {
      console.error('Error loading hospitals:', error);
    } finally {
      setLoadingHospitals(false);
    }
  };

  const loadOperatingRooms = async () => {
    try {
      const response = await getOperatingRooms();
      setOperatingRooms(response.operatingRooms || []);
    } catch (error) {
      console.error('Error loading operating rooms:', error);
    }
  };

  const roomsFor = (hospitalId: string) =>
    operatingRooms
      .filter(r => r.hospitalId === hospitalId)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const res: any = await createHospital({
        ...formData,
        ...(createUseColor && createColor ? { color: createColor } : {}),
        // Always send both site-type flags on create, so a brand-new hospital is explicitly
        // classified from day one and never falls back to the deprecated name heuristic.
        isSurgeryCenter: createIsSurgeryCenter,
        isChildrensHospital: createIsChildrensHospital,
      });
      const created = res.hospital;
      // Auto-create the requested number of generic ORs (OR 1 … OR N).
      const n = Number(createNumORs);
      let madeRooms = 0;
      if (created?.hospitalId && Number.isInteger(n) && n > 0) {
        for (let i = 1; i <= n; i++) {
          await createOperatingRoom({ roomNumber: `OR ${i}`, name: `OR ${i}`, hospitalId: created.hospitalId });
          madeRooms++;
        }
      }
      setMessage({ type: 'success', text: `Hospital created${madeRooms ? ` with ${madeRooms} operating room${madeRooms > 1 ? 's' : ''}` : ''}!` });
      setFormData({ name: '', address: { street: '', city: '', state: '', zipCode: '', country: 'USA' }, latitude: undefined, longitude: undefined });
      setCreateUseColor(false); setCreateColor('#3b82f6'); setCreateNumORs('');
      setCreateIsSurgeryCenter(false); setCreateIsChildrensHospital(false);
      loadHospitals();
      loadOperatingRooms();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to create hospital' });
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (hospital: any) => { setEditingHospitalId(hospital.hospitalId); setEditingName(hospital.name); setMessage(null); };
  const cancelEdit = () => { setEditingHospitalId(null); setEditingName(''); };
  const saveEdit = async (hospitalId: string) => {
    const trimmed = editingName.trim();
    if (!trimmed) { setMessage({ type: 'error', text: 'Hospital name cannot be empty.' }); return; }
    setSavingEdit(true); setMessage(null);
    try {
      await updateHospital(hospitalId, trimmed);
      setHospitals((prev) => prev.map((h) => (h.hospitalId === hospitalId ? { ...h, name: trimmed } : h)));
      setEditingHospitalId(null); setEditingName('');
      setMessage({ type: 'success', text: 'Hospital renamed successfully!' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update hospital' });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteHospital = async (hospital: any) => {
    setDeletingHospitalId(hospital.hospitalId);
    setMessage(null);
    try {
      // First ask the backend exactly what would be removed, so the confirmation is honest.
      const preview = await deleteHospital(hospital.hospitalId, { dryRun: true });
      const w = (preview.wouldDelete || {}) as HospitalDeleteSummary;
      const lines = [
        `Delete “${hospital.name}” and everything under it?`,
        '',
        'This will permanently remove:',
        `  • the hospital itself`,
        `  • ${w.operatingRooms ?? 0} operating room(s)`,
        `  • ${w.schedules ?? 0} scheduled case(s)`,
        `  • ${w.allocations ?? 0} allocation(s)`,
        `  • ${w.distanceRows ?? 0} hospital-distance link(s)`,
        '',
        `${w.employeesHomeBaseCleared ?? 0} staff member(s) list this as their home base — they will be KEPT, with their home base cleared.`,
        '',
        'This cannot be undone. Continue?',
      ];
      if (!window.confirm(lines.join('\n'))) {
        setDeletingHospitalId(null);
        return;
      }

      const res = await deleteHospital(hospital.hospitalId);
      const d = (res.deleted || w) as HospitalDeleteSummary;

      // Clear any UI state that pointed at the now-deleted hospital.
      if (editingHospitalId === hospital.hospitalId) { setEditingHospitalId(null); setEditingName(''); }
      if (expandedHospitalId === hospital.hospitalId) setExpandedHospitalId(null);

      await Promise.all([loadHospitals(), loadOperatingRooms()]);
      setMessage({
        type: 'success',
        text: `Deleted “${hospital.name}” — removed ${d.operatingRooms ?? 0} room(s), ${d.schedules ?? 0} case(s), ${d.allocations ?? 0} allocation(s); cleared home base for ${d.employeesHomeBaseCleared ?? 0} staff.`,
      });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || error.response?.data?.message || 'Failed to delete hospital' });
    } finally {
      setDeletingHospitalId(null);
    }
  };

  const saveColor = async (hospitalId: string, color: string) => {
    const hosp = hospitals.find((h) => h.hospitalId === hospitalId);
    if (!hosp) return;
    const prevColor = hosp.color;
    setSavingColorId(hospitalId); setMessage(null);
    setHospitals((prev) => prev.map((h) => (h.hospitalId === hospitalId ? { ...h, color } : h)));
    try {
      await updateHospital(hospitalId, hosp.name, color);
      setMessage({ type: 'success', text: `Board color updated for ${hosp.name}.` });
    } catch (error: any) {
      setHospitals((prev) => prev.map((h) => (h.hospitalId === hospitalId ? { ...h, color: prevColor } : h)));
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update color' });
    } finally {
      setSavingColorId(null);
    }
  };

  const resetColor = async (hospitalId: string) => {
    const hosp = hospitals.find((h) => h.hospitalId === hospitalId);
    if (!hosp) return;
    const prevColor = hosp.color;
    setSavingColorId(hospitalId); setMessage(null);
    setHospitals((prev) => prev.map((h) => (h.hospitalId === hospitalId ? { ...h, color: undefined } : h)));
    try {
      await updateHospital(hospitalId, hosp.name, '');
      setMessage({ type: 'success', text: `Reset ${hosp.name} to its default color.` });
    } catch (error: any) {
      setHospitals((prev) => prev.map((h) => (h.hospitalId === hospitalId ? { ...h, color: prevColor } : h)));
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to reset color' });
    } finally {
      setSavingColorId(null);
    }
  };

  // SITE TYPE — pins whether Symphony treats this site as a surgery center / children's hospital.
  // The two boxes do NOT behave the same way when never set:
  //   - Surgery center: the backend falls back to a deprecated hospital-NAME heuristic, so a rename
  //     can quietly change staffing until the box is ticked (or deliberately un-ticked).
  //   - Children's: there is no name fallback at all (Braden 2026-08-09, answer #2). An unticked box
  //     means a general ADULT site — any anesthesiologist may supervise and the pediatric 1:3 cap
  //     does not apply — no matter what the site is called. This box is the only lever there is.
  // Optimistic with rollback, matching saveColor.
  const saveSiteType = async (
    hospitalId: string,
    patch: { isSurgeryCenter?: boolean; isChildrensHospital?: boolean }
  ) => {
    const hosp = hospitals.find((h) => h.hospitalId === hospitalId);
    if (!hosp) return;
    const prev = { isSurgeryCenter: hosp.isSurgeryCenter, isChildrensHospital: hosp.isChildrensHospital };
    setSavingSiteTypeId(hospitalId); setMessage(null);
    setHospitals((list) => list.map((h) => (h.hospitalId === hospitalId ? { ...h, ...patch } : h)));
    try {
      await updateHospital(hospitalId, hosp.name, undefined, patch);
      const label = patch.isSurgeryCenter !== undefined ? 'Surgery center' : "Children's hospital";
      const on = patch.isSurgeryCenter !== undefined ? patch.isSurgeryCenter : patch.isChildrensHospital;
      setMessage({ type: 'success', text: `${label} ${on ? 'set' : 'cleared'} for ${hosp.name}.` });
    } catch (error: any) {
      setHospitals((list) => list.map((h) => (h.hospitalId === hospitalId ? { ...h, ...prev } : h)));
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to update site type' });
    } finally {
      setSavingSiteTypeId(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({ ...formData, address: { ...formData.address, [addressField]: value } });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // --- Operating-room actions ---
  const toggleExpand = (hospitalId: string) => {
    setExpandedHospitalId((prev) => (prev === hospitalId ? null : hospitalId));
    setRoomPreset('Generic OR'); setRoomCustom(''); setRoomCount('1'); setRoomNameEdits({}); setMessage(null);
  };

  const addRooms = async (hospitalId: string) => {
    const label = presetLabel(roomPreset, roomCustom);
    if (!label) { setMessage({ type: 'error', text: 'Enter a name for the custom room type.' }); return; }
    const n = Number(roomCount);
    if (!Number.isInteger(n) || n < 1) { setMessage({ type: 'error', text: 'Enter how many rooms (1 or more).' }); return; }
    // Continue numbering from any existing rooms of this type. Single items get no number.
    const re = new RegExp('^' + escapeRe(label) + '(?: (\\d+))?$', 'i');
    const existing = operatingRooms.filter((r) => r.hospitalId === hospitalId && re.test(r.name)).length;
    const names = (existing === 0 && n === 1) ? [label] : Array.from({ length: n }, (_, i) => `${label} ${existing + i + 1}`);
    setAddingRooms(true); setMessage(null);
    try {
      for (const nm of names) {
        await createOperatingRoom({ roomNumber: nm, name: nm, hospitalId });
      }
      await loadOperatingRooms();
      setRoomCount('1'); setRoomCustom('');
      setMessage({ type: 'success', text: `Added ${names.length} room${names.length > 1 ? 's' : ''}: ${names.join(', ')}` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to add rooms' });
    } finally {
      setAddingRooms(false);
    }
  };

  const commitRename = async (room: any) => {
    const v = (roomNameEdits[room.operatingRoomId] ?? room.name).trim();
    setRoomNameEdits((p) => { const n = { ...p }; delete n[room.operatingRoomId]; return n; });
    if (!v || v === room.name) return;
    setBusyRoomId(room.operatingRoomId); setMessage(null);
    setOperatingRooms((prev) => prev.map((r) => (r.operatingRoomId === room.operatingRoomId ? { ...r, name: v, roomNumber: v } : r)));
    try {
      await updateOperatingRoom(room.operatingRoomId, { name: v, roomNumber: v });
      setMessage({ type: 'success', text: `Renamed to “${v}”.` });
    } catch (error: any) {
      setOperatingRooms((prev) => prev.map((r) => (r.operatingRoomId === room.operatingRoomId ? room : r)));
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to rename room' });
    } finally {
      setBusyRoomId(null);
    }
  };

  const deleteRoom = async (room: any) => {
    if (!window.confirm(`Delete “${room.name}”? This can’t be undone.`)) return;
    setBusyRoomId(room.operatingRoomId); setMessage(null);
    try {
      await deleteOperatingRoom(room.operatingRoomId);
      setOperatingRooms((prev) => prev.filter((r) => r.operatingRoomId !== room.operatingRoomId));
      setMessage({ type: 'success', text: `Deleted “${room.name}”.` });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete room' });
    } finally {
      setBusyRoomId(null);
    }
  };

  const renderRoomsPanel = (hospital: any, rooms: any[]) => (
    <div style={{ padding: '12px 14px', background: 'var(--bg-surface-raised)', borderRadius: '8px', margin: '4px 0' }}>
      <h4 style={{ margin: '0 0 10px' }}>Operating rooms at {hospital.name} ({rooms.length})</h4>
      <AdminOnly>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
          <select value={roomPreset} onChange={(e) => setRoomPreset(e.target.value)} className="form-control" style={{ maxWidth: '170px' }}>
            {ROOM_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {roomPreset === 'Custom…' && (
            <input value={roomCustom} onChange={(e) => setRoomCustom(e.target.value)} placeholder="Room name (e.g. Hybrid)" className="form-control" style={{ maxWidth: '170px' }} />
          )}
          <label style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>How many:</label>
          <input type="number" min="1" value={roomCount} onChange={(e) => setRoomCount(e.target.value)} className="form-control" style={{ width: '70px' }} />
          <button type="button" className="btn btn-primary" onClick={() => addRooms(hospital.hospitalId)} disabled={addingRooms}>
            {addingRooms ? 'Adding…' : 'Add rooms'}
          </button>
        </div>
        <small style={{ display: 'block', color: 'var(--text-muted)', marginBottom: '8px' }}>
          “Endo” × 3 → Endo 1, Endo 2, Endo 3. A single MRI/CT gets no number. Rename or delete any room below.
        </small>
      </AdminOnly>
      {rooms.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>No rooms yet.</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '6px', marginTop: '8px' }}>
          {rooms.map((room) => (
            <div key={room.operatingRoomId} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <AdminOnly fallback={<span style={{ fontSize: '0.9em' }}>{room.name}</span>}>
                <input
                  value={roomNameEdits[room.operatingRoomId] ?? room.name}
                  onChange={(e) => setRoomNameEdits((p) => ({ ...p, [room.operatingRoomId]: e.target.value }))}
                  onBlur={() => commitRename(room)}
                  onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                  disabled={busyRoomId === room.operatingRoomId}
                  className="form-control"
                  style={{ padding: '3px 8px', fontSize: '0.85em', flex: 1, minWidth: 0 }}
                  aria-label={`Rename ${room.name}`}
                />
                <button
                  type="button"
                  onClick={() => deleteRoom(room)}
                  disabled={busyRoomId === room.operatingRoomId}
                  title={`Delete ${room.name}`}
                  style={{ border: '1px solid var(--border-soft)', background: 'var(--bg-surface)', color: 'var(--danger-text, #dc2626)', borderRadius: '6px', width: '26px', height: '26px', cursor: 'pointer', flexShrink: 0 }}
                >
                  ✕
                </button>
              </AdminOnly>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="card">
      <h2>Hospitals &amp; Operating Rooms</h2>
      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'error' : 'success'}`}>{message.text}</div>
      )}

      <ReadOnlyBanner />

      <AdminOnly>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Hospital Name</label>
          <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="e.g., St. Mary's Hospital" />
        </div>

        <h3>Address</h3>

        <div className="form-group">
          <label htmlFor="address.street">Street Address</label>
          <input type="text" id="address.street" name="address.street" value={formData.address.street} onChange={handleChange} required placeholder="790 Fairhaven Street" />
        </div>

        <div className="grid grid-2">
          <div className="form-group">
            <label htmlFor="address.city">City</label>
            <input type="text" id="address.city" name="address.city" value={formData.address.city} onChange={handleChange} required placeholder="New York" />
          </div>
          <div className="form-group">
            <label htmlFor="address.state">State</label>
            <input type="text" id="address.state" name="address.state" value={formData.address.state} onChange={handleChange} required placeholder="NY" />
          </div>
        </div>

        <div className="grid grid-2">
          <div className="form-group">
            <label htmlFor="address.zipCode">ZIP Code</label>
            <input type="text" id="address.zipCode" name="address.zipCode" value={formData.address.zipCode} onChange={handleChange} required placeholder="10001" />
          </div>
          <div className="form-group">
            <label htmlFor="address.country">Country</label>
            <input type="text" id="address.country" name="address.country" value={formData.address.country} onChange={handleChange} required placeholder="USA" />
          </div>
        </div>

        <div className="grid grid-2">
          <div className="form-group">
            <label htmlFor="latitude">Latitude</label>
            <input type="number" id="latitude" name="latitude" value={formData.latitude ?? ''} onChange={(e) => { const value = e.target.value === '' ? undefined : parseFloat(e.target.value); setFormData({ ...formData, latitude: value }); }} step="0.000001" min="-90" max="90" placeholder="e.g., 40.7128" />
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>Optional. Latitude between -90 and 90</small>
          </div>
          <div className="form-group">
            <label htmlFor="longitude">Longitude</label>
            <input type="number" id="longitude" name="longitude" value={formData.longitude ?? ''} onChange={(e) => { const value = e.target.value === '' ? undefined : parseFloat(e.target.value); setFormData({ ...formData, longitude: value }); }} step="0.000001" min="-180" max="180" placeholder="e.g., -74.0060" />
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>Optional. Longitude between -180 and 180</small>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="createNumORs">Number of Operating Rooms</label>
          <input type="number" id="createNumORs" min="0" value={createNumORs} onChange={(e) => setCreateNumORs(e.target.value)} placeholder="e.g., 8" style={{ maxWidth: '160px' }} />
          <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
            Optional. Creates that many rooms (OR 1 … OR N). You can rename them or add typed rooms (Endo, C/S, MRI…) below after creating.
          </small>
        </div>

        <div className="form-group">
          <label>Board Color</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal', cursor: 'pointer' }}>
            <input type="checkbox" checked={createUseColor} onChange={(e) => setCreateUseColor(e.target.checked)} style={{ width: 'auto' }} />
            Pick a custom color (otherwise a distinct one is auto-assigned)
          </label>
          {createUseColor && (
            <input type="color" value={createColor} onChange={(e) => setCreateColor(e.target.value)} aria-label="Board color for the new hospital" style={{ width: '48px', height: '30px', padding: 0, border: '1px solid var(--border-soft)', borderRadius: '6px', cursor: 'pointer', background: 'none', marginTop: '8px' }} />
          )}
        </div>

        <div className="form-group">
          <label>Site Type</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal', cursor: 'pointer' }}>
            <input type="checkbox" checked={createIsSurgeryCenter} onChange={(e) => setCreateIsSurgeryCenter(e.target.checked)} style={{ width: 'auto' }} />
            Surgery center
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal', cursor: 'pointer' }}>
            <input type="checkbox" checked={createIsChildrensHospital} onChange={(e) => setCreateIsChildrensHospital(e.target.checked)} style={{ width: 'auto' }} />
            Children’s hospital
          </label>
          <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
            Drives allocation: surgery centers are staffed first with travel-aware provider matching, and
            children’s hospitals may only be supervised by pediatric anesthesiologists. Changeable later.
          </small>
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create Hospital'}
        </button>
      </form>
      </AdminOnly>

      <div className="hospitals-table">
        <h3>Existing Hospitals &amp; Rooms</h3>
        <AdminOnly>
          <small style={{ display: 'block', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Use <strong>Rename</strong> in the Actions column to edit a hospital’s name, or <strong>Delete</strong> to remove a
            hospital and everything under it (rooms, cases, and allocations). Deleting asks you to confirm and shows exactly
            what will be removed first.
          </small>
        </AdminOnly>
        {loadingHospitals ? (
          <p>Loading hospitals...</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Hospital ID</th>
                <th>Name</th>
                <th>Board Color</th>
                <th>Site Type</th>
                <th>Operating Rooms</th>
                <th>Address</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hospitals.map((hospital) => {
                const isEditing = editingHospitalId === hospital.hospitalId;
                const expanded = expandedHospitalId === hospital.hospitalId;
                const rooms = roomsFor(hospital.hospitalId);
                return (
                  <React.Fragment key={hospital.hospitalId}>
                    <tr>
                      <td>{hospital.hospitalId}</td>
                      <td>
                        {isEditing ? (
                          <input type="text" value={editingName} onChange={(e) => setEditingName(e.target.value)} disabled={savingEdit} autoFocus aria-label="Hospital name" />
                        ) : (
                          hospital.name
                        )}
                      </td>
                      <td>
                        <AdminOnly>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="color" value={colorForHospital(hospital)} onChange={(e) => saveColor(hospital.hospitalId, e.target.value)} disabled={savingColorId === hospital.hospitalId} title="Board color for this hospital" aria-label={`Board color for ${hospital.name}`} style={{ width: '42px', height: '28px', padding: 0, border: '1px solid var(--border-soft)', borderRadius: '6px', cursor: 'pointer', background: 'none' }} />
                            {hospital.color && (
                              <button type="button" className="btn" onClick={() => resetColor(hospital.hospitalId)} disabled={savingColorId === hospital.hospitalId} title="Clear the custom color and use the auto-assigned default" style={{ fontSize: '0.75em', padding: '2px 8px' }}>Reset</button>
                            )}
                            {savingColorId === hospital.hospitalId && (<span style={{ fontSize: '0.75em', color: 'var(--text-muted)' }}>Saving…</span>)}
                          </div>
                        </AdminOnly>
                      </td>
                      <td>
                        <AdminOnly>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', fontSize: '0.85em', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <input
                                type="checkbox"
                                checked={!!hospital.isSurgeryCenter}
                                onChange={(e) => saveSiteType(hospital.hospitalId, { isSurgeryCenter: e.target.checked })}
                                disabled={savingSiteTypeId === hospital.hospitalId}
                                style={{ width: 'auto' }}
                                aria-label={`Mark ${hospital.name} as a surgery center`}
                              />
                              Surgery center
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', fontSize: '0.85em', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              <input
                                type="checkbox"
                                checked={!!hospital.isChildrensHospital}
                                onChange={(e) => saveSiteType(hospital.hospitalId, { isChildrensHospital: e.target.checked })}
                                disabled={savingSiteTypeId === hospital.hospitalId}
                                style={{ width: 'auto' }}
                                aria-label={`Mark ${hospital.name} as a children's hospital`}
                              />
                              Children’s
                            </label>
                            {/* THE TWO UNSET STATES MEAN DIFFERENT THINGS, so they get different notices. One
                                shared "guessed from name" line was actively misleading for the Children's box:
                                an admin reading it would leave a pediatric-sounding site unticked believing the
                                name still made it pediatric, and Symphony would staff it with general
                                anesthesiologists at 1:4. This is the surface that is supposed to prevent that,
                                and the backend's warning for it only reaches CloudWatch. */}
                            {hospital.isSurgeryCenter === undefined && (
                              <span style={{ fontSize: '0.7em', color: 'var(--text-muted)' }} title="With this box never set, the backend still guesses surgery-center status from the hospital name, so a rename can change staffing. Tick or untick it to set it explicitly.">
                                Surgery center: not set — guessed from name
                              </span>
                            )}
                            {hospital.isChildrensHospital === undefined && (
                              <span style={{ fontSize: '0.7em', color: 'var(--text-muted)' }} title="Pediatric status comes from this box alone — the hospital name is never consulted. Left unticked, this site is staffed as a general adult site: any anesthesiologist may supervise and the pediatric 1:3 cap does not apply.">
                                Children’s: not set — staffed as a general adult site
                              </span>
                            )}
                            {savingSiteTypeId === hospital.hospitalId && (<span style={{ fontSize: '0.7em', color: 'var(--text-muted)' }}>Saving…</span>)}
                          </div>
                        </AdminOnly>
                      </td>
                      <td>
                        <button type="button" className="btn" onClick={() => toggleExpand(hospital.hospitalId)} style={{ whiteSpace: 'nowrap' }}>
                          {rooms.length} room{rooms.length === 1 ? '' : 's'} {expanded ? '▴' : '▾'}
                        </button>
                      </td>
                      <td>{hospital.address.street}, {hospital.address.city}, {hospital.address.state} {hospital.address.zipCode}, {hospital.address.country}</td>
                      <td>{hospital.latitude !== undefined ? hospital.latitude.toFixed(6) : 'N/A'}</td>
                      <td>{hospital.longitude !== undefined ? hospital.longitude.toFixed(6) : 'N/A'}</td>
                      <td>
                        <AdminOnly>
                          {isEditing ? (
                            <>
                              <button type="button" className="btn btn-primary" onClick={() => saveEdit(hospital.hospitalId)} disabled={savingEdit} style={{ marginRight: '6px' }}>{savingEdit ? 'Saving...' : 'Save'}</button>
                              <button type="button" className="btn" onClick={cancelEdit} disabled={savingEdit}>Cancel</button>
                            </>
                          ) : (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              <button type="button" className="btn" onClick={() => startEdit(hospital)} disabled={editingHospitalId !== null || deletingHospitalId !== null} title={`Rename ${hospital.name}`}>Rename</button>
                              <button
                                type="button"
                                className="btn"
                                onClick={() => handleDeleteHospital(hospital)}
                                disabled={editingHospitalId !== null || deletingHospitalId !== null}
                                title={`Delete ${hospital.name} and all of its rooms, cases, and allocations`}
                                style={{ color: 'var(--danger-text, #dc2626)', borderColor: 'var(--danger-text, #dc2626)' }}
                              >
                                {deletingHospitalId === hospital.hospitalId ? 'Deleting…' : 'Delete'}
                              </button>
                            </div>
                          )}
                        </AdminOnly>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={9} style={{ background: 'transparent', padding: '0 4px' }}>
                          {renderRoomsPanel(hospital, rooms)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default HospitalForm;
